import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  handleError,
  Role,
  Asset,
  AttributeSensitivity,
  formatDatasetPath,
  resolveGlossaryAssetSubmit,
} from '../helpers';
import { DataTable, MainColumn, ObjectDetails, Tabs, SkeletonLoader } from '../components';
import {
  useSideDialog,
  useModalDialog,
  useQueryParams,
  useGlobalState,
  useMessage,
} from '../hooks';
import {
  entityService,
  datasetService,
  datasetGroupService,
  datasourceService,
  attributeService,
  glossaryService,
  systemService,
  userService,
} from '../services';

export const Profile = () => {
  const { queryParams, resetParams } = useQueryParams();

  const [user, setUser] = useState([]);
  const [loggedInUser, setLoggedInUser] = useGlobalState('loggedInUser');
  const [selectedItems, setSelectedItems] = useState([]);
  const [ownedItems, setOwnedItems] = useState([]);
  const [ownedItemsCount, setOwnedItemsCount] = useState([]);
  const [ownedItemsTotalCount, setOwnedItemsTotalCount] = useState();
  const [loading, setLoading] = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [defaultSelectedObject, setDefaultSelectedObject] = useState({ tab: queryParams.object });

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();

  const navigate = useNavigate();

  const user_id = loggedInUser.user_id;

  useEffect(() => {
    fetchItems();
    fetchOwnedItemsCount();
  }, []);

  const fetchItems = async () => {
    userService
      .getById(user_id, true)
      .then(res => {
        setUser(res);
        setLoading(false);
      })
      .catch(err => {
        showMessage(err, 'error');
        setLoading(false);
      });
  };

  const fetchOwnedItemsCount = async () => {
    userService
      .getOwnedItemsCount(user_id)
      .then(res => {
        let tmp = {};
        let defaultObject;

        const objectSortOrder = [
          'attribute',
          'entity',
          'glossary',
          'dataset',
          'dataset_group',
          'datasource',
          'system',
        ];
        res.owned_items_count.sort(
          (a, b) => objectSortOrder.indexOf(a.object_name) - objectSortOrder.indexOf(b.object_name)
        );

        res.owned_items_count.forEach(result => {
          if (result.owned_items_count > 0 && defaultObject === undefined) {
            defaultObject = result.object_name;
          }
          tmp = { ...tmp, [result.object_name]: parseInt(result.owned_items_count) };
        });

        defaultObject = queryParams.object || defaultObject;

        setDefaultSelectedObject({ tab: defaultObject });
        fetchOwnedItems(defaultObject);
        setOwnedItemsCount(tmp);

        setOwnedItemsTotalCount(
          (tmp.attribute || 0) +
            (tmp.dataset || 0) +
            (tmp.dataset_group || 0) +
            (tmp.datasource || 0) +
            (tmp.system || 0) +
            (tmp.entity || 0) +
            (tmp.glossary || 0)
        );
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const fetchOwnedItems = async objectName => {
    if (objectName) {
      // objectName is undefined if user does not own any items
      setLoadingTable(true);
      userService
        .getOwnedItems(user_id, objectName)
        .then(res => {
          let result = res.owned_items;

          if (objectName === Asset.Attribute) {
            // The PII icon column renders a label and its tooltip rather than the raw flag.
            result = result.map(x => ({
              ...x,
              pii: x.is_pii ? 'PII' : null,
              pii_tooltip: x.is_pii
                ? 'Classified as Personally Identifiable Information (PII)'
                : null,
            }));
          }

          if (objectName === Asset.Dataset) {
            // Render each owned dataset as its full endpoint path (API datasets); the ancestors
            // are attached to the row by the owned-items endpoint.
            result = result.map(x => ({
              ...x,
              dataset_path: formatDatasetPath(x, { ancestors: x.ancestors }),
            }));
          }

          setOwnedItems(result);
        })
        .catch(err => {
          showMessage(err, 'error');
        })
        .finally(res => setLoadingTable(false));
    } else {
      setLoadingTable(false); // Needed to show "no result" message
    }
  };

  const editProfile = async user => {
    const users = { users: [user] };

    await userService
      .updateProfile(users)
      .then(async res => {
        fetchItems();
        showMessage(res.message);
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };
  const editDataset = async data => {
    const items = { datasets: Array.isArray(data) ? data : [data] };

    await datasetService
      .update(items)
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        showMessage(err, 'error');
        throw err;
      });
  };

  const editDatasetGroup = async data => {
    const items = { dataset_groups: Array.isArray(data) ? data : [data] };

    await datasetGroupService
      .update(items)
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        showMessage(err, 'error');
        throw err;
      });
  };

  const editDatasource = async data => {
    const items = { datasources: Array.isArray(data) ? data : [data] };

    await datasourceService
      .update(items)
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editSystem = async data => {
    const items = { systems: Array.isArray(data) ? data : [data] };

    await systemService
      .update(items)
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editGlossary = async data => {
    const items = { glossaries: Array.isArray(data) ? data : [data] };

    await glossaryService
      .update(items)
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        showMessage(err, 'error');
        throw err;
      });
  };

  /**
   * Saves an edited glossary asset. One handler serves both kinds: the form's role picker can move an
   * asset between Entity and Attribute, so which resource to call is decided from the submitted row
   * rather than from the button that opened the dialog.
   */
  const editGlossaryAsset = async data => {
    const { service, payload, isCreate } = resolveGlossaryAssetSubmit(data, 'edit');

    await (isCreate ? service.create(payload) : service.update(payload))
      .then(async res => {
        fetchOwnedItemsCount();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        showMessage(err, 'error');
        throw err;
      });
  };

  const deleteEntity = async data => {
    await entityService
      .destroy({ entities: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteDataset = async data => {
    await datasetService
      .destroy({ datasets: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteDatasetGroup = async data => {
    await datasetGroupService
      .destroy({ dataset_groups: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteDatasource = async data => {
    await datasourceService
      .destroy({ datasources: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteAttribute = async data => {
    await attributeService
      .destroy({ attributes: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteGlossary = async data => {
    await glossaryService
      .destroy({ glossaries: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const deleteSystem = async data => {
    await systemService
      .destroy({ systems: data })
      .then(async res => {
        showMessage(res.message);
        hideModalDialog();
        fetchOwnedItemsCount();
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const changePassword = async item => {
    hideSideDialog();
    navigate('/change_password');
  };

  const onObjectChange = object => {
    resetParams({ object: object });
    fetchOwnedItems(object);
    clearSelectedItems();
  };

  const data = loading ? [] : user.users[0];

  const editableAdmin = loggedInUser && loggedInUser.user_role_name === Role.admin;
  const editable =
    loggedInUser &&
    (loggedInUser.user_role_name === Role.admin || loggedInUser.user_role_name === Role.editor);

  return (
    <div className="columns narrow-margin">
      <MainColumn>
        <ObjectDetails
          type="USER"
          title={data.user_fullname}
          secondaryTitle={`${data.user_title ? data.user_title : ''}${data.user_department ? '\n' + data.user_department : ''}`}
          photo={data.user_photo}
          subtitleText={data && !loading && data.user_username}
          showDescription={false}
          loading={loading}
          editable={editableAdmin}
          buttons={
            data.user_auth_method === 'local' && [
              {
                buttonStyle: 'main-button',
                value: 'Edit',
                onClick: () => showSideDialog('editProfile', user.users, editProfile),
              },
              {
                value: 'CHANGE PASSWORD',
                onClick: changePassword,
              },
            ]
          }
        >
          <h3>{!loading ? 'Email' : <SkeletonLoader width="5vw" />}</h3>
          <div className="display-linebreak">
            {!loading ? (
              data.user_email === null ? (
                '-'
              ) : (
                <a className="link" href={'mailto:' + data.user_email}>
                  {data.user_email}
                </a>
              )
            ) : (
              <SkeletonLoader width="10vw" />
            )}
          </div>

          <h3>{!loading ? 'Role' : <SkeletonLoader width="5vw" />}</h3>
          <div className="display-linebreak">
            {!loading ? (
              data.user_role_name === null ? (
                '-'
              ) : (
                data.user_role_name
              )
            ) : (
              <SkeletonLoader width="10vw" />
            )}
          </div>
        </ObjectDetails>
      </MainColumn>

      <div className="column">
        <Tabs className="slim left" disableTabsWithoutResults={false}>
          <div label="Assigned Assets" tabid="owned_assets" resultCount={ownedItemsTotalCount}>
            {ownedItemsTotalCount > 0 ? (
              <Tabs
                className="buttons left"
                onTabChange={onObjectChange}
                showResultCount={false}
                disableTabsWithoutResults={true}
                defaultSelectedTab={defaultSelectedObject}
              >
                <div
                  label="Attributes"
                  tabid={Asset.Attribute}
                  resultCount={(ownedItemsCount && ownedItemsCount.attribute) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'attribute_role_name',
                        name: '',
                        type: 'icon',
                        tooltip: 'attribute_role_name',
                      },
                      {
                        id: 'attribute_name',
                        name: 'Name',
                        link: '/attributes/:attribute_id',
                        tooltip: 'attribute_description',
                        className: 'bold',
                      },
                      {
                        id: 'attribute_unit_code',
                        name: 'Attribute Unit',
                        tooltip: 'attribute_unit_name',
                      },
                      {
                        id: 'attribute_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        // An attribute belongs to a glossary, and the same name can be used in another one.
                        id: 'glossary_name',
                        name: 'Glossary',
                        link: '/glossaries/:glossary_id',
                      },
                      {
                        id: 'pii',
                        name: '',
                        type: 'icon',
                        tooltip: 'pii_tooltip',
                        className: 'no-right-margin',
                      },
                      {
                        id: 'attribute_sensitivity_name',
                        name: 'Data Sensitivity',
                        type: 'pill',
                        value: 'attribute_sensitivity_level',
                        values: AttributeSensitivity,
                        tooltip: 'attribute_sensitivity_name',
                      },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog(
                            'editAttribute',
                            items || selectedItems,
                            editGlossaryAsset
                          ),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog(
                            'deleteAttribute',
                            items || selectedItems,
                            deleteAttribute
                          ),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="attribute_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'attribute_name',
                      'attribute_description',
                      'attribute_role_name',
                      'attribute_unit_name',
                      'glossary_name',
                      'attribute_sensitivity_name',
                      'is_pii',
                      'assigned_as',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Entities"
                  tabid={Asset.Entity}
                  resultCount={(ownedItemsCount && ownedItemsCount.entity) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'entity_name',
                        name: 'Name',
                        link: '/glossaries/:glossary_id/:entity_id',
                        tooltip: 'entity_description',
                        className: 'bold',
                      },
                      {
                        id: 'entity_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      { id: 'glossary_name', name: 'Glossary', link: '/glossaries/:glossary_id' },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editEntity', items || selectedItems, editGlossaryAsset),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog('deleteEntity', items || selectedItems, deleteEntity),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="entity_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'entity_name',
                      'entity_description',
                      'glossary_name',
                      'assigned_as',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Glossaries"
                  tabid={Asset.Glossary}
                  resultCount={(ownedItemsCount && ownedItemsCount.glossary) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'glossary_name',
                        name: 'Name',
                        link: '/glossaries/:glossary_id',
                        tooltip: 'glossary_description',
                        className: 'bold',
                      },
                      { id: 'glossary_type', name: 'Type', className: 'small-text' },
                      {
                        id: 'glossary_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editGlossary', items || selectedItems, editGlossary),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog('deleteGlossary', items || selectedItems, deleteGlossary),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="glossary_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editableAdmin}
                    filterable={true}
                    filterObjects={[
                      'glossary_name',
                      'glossary_type',
                      'glossary_description',
                      'assigned_as',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Datasets"
                  tabid={Asset.Dataset}
                  resultCount={(ownedItemsCount && ownedItemsCount.dataset) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'dataset_type_name',
                        name: '',
                        type: 'icon',
                        tooltip: 'dataset_type_name',
                      },
                      {
                        // dataset_path holds the formatDatasetPath() output (its segments link
                        // themselves); sort still keys off dataset_name.
                        id: 'dataset_path',
                        name: 'Name',
                        sortField: 'dataset_name',
                        className: 'bold',
                      },
                      {
                        id: 'dataset_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        id: 'dataset_group_name',
                        name: 'Dataset Group',
                        link: '/systems/:system_id/:datasource_id/:dataset_group_id',
                      },
                      {
                        id: 'datasource_type_category',
                        name: '',
                        type: 'icon',
                        tooltip: 'datasource_type_name',
                        className: 'no-right-margin',
                      },
                      {
                        id: 'datasource_name',
                        name: 'Datasource',
                        link: '/systems/:system_id/:datasource_id',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'system_name',
                        name: 'System',
                        link: '/systems/:system_id',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editDataset', items || selectedItems, editDataset),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog('deleteDataset', items || selectedItems, deleteDataset),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="dataset_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'dataset_name',
                      'dataset_description',
                      'dataset_type_name',
                      'dataset_group_name',
                      'datasource_name',
                      'system_name',
                      'assigned_as',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Dataset Groups"
                  tabid={Asset.DatasetGroup}
                  resultCount={(ownedItemsCount && ownedItemsCount.dataset_group) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'dataset_group_name',
                        name: 'Name',
                        link: '/systems/:system_id/:datasource_id/:dataset_group_id',
                        tooltip: 'dataset_group_description',
                        className: 'bold',
                      },
                      {
                        id: 'dataset_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        id: 'datasource_type_category',
                        name: '',
                        type: 'icon',
                        tooltip: 'datasource_type_name',
                        className: 'no-right-margin',
                      },
                      {
                        id: 'datasource_name',
                        name: 'Datasource',
                        link: '/systems/:system_id/:datasource_id',
                      },
                      {
                        id: 'system_name',
                        name: 'System',
                        link: '/systems/:system_id',
                        viewMode: 'statistics',
                      },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog(
                            'editDatasetGroup',
                            items || selectedItems,
                            editDatasetGroup
                          ),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog(
                            'deleteDatasetGroup',
                            items || selectedItems,
                            deleteDatasetGroup
                          ),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="dataset_group_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editable}
                    filterable={true}
                    filterObjects={[
                      'dataset_group_name',
                      'dataset_description',
                      'datasource_name',
                      'system_name',
                      'assigned_as',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Datasources"
                  tabid={Asset.Datasource}
                  resultCount={(ownedItemsCount && ownedItemsCount.datasource) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'datasource_type_category',
                        name: '',
                        type: 'icon',
                        tooltip: 'datasource_type_name',
                      },
                      {
                        id: 'datasource_name',
                        name: 'Name',
                        link: '/systems/:system_id/:datasource_id',
                        tooltip: 'datasource_description',
                        className: 'bold',
                      },
                      {
                        id: 'datasource_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      { id: 'system_name', name: 'System', link: '/systems/:system_id' },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editDatasource', items || selectedItems, editDatasource),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog(
                            'deleteDatasource',
                            items || selectedItems,
                            deleteDatasource
                          ),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="datasource_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editableAdmin}
                    filterable={true}
                    filterObjects={[
                      'datasource_name',
                      'datasource_description',
                      'datasource_type_category',
                      'datasource_type_name',
                      'system_name',
                      'assigned_as',
                    ]}
                    showViewModes={true}
                  />
                </div>

                <div
                  label="Systems"
                  tabid={Asset.System}
                  resultCount={(ownedItemsCount && ownedItemsCount.system) || 0}
                >
                  <DataTable
                    columns={[
                      {
                        id: 'system_name',
                        name: 'Name',
                        link: '/systems/:system_id',
                        tooltip: 'system_description',
                        className: 'bold',
                      },
                      { id: 'system_code', name: 'Code' },
                      { id: 'system_type', name: 'Type', className: 'small-text' },
                      {
                        id: 'system_description',
                        name: 'Description',
                        type: 'description',
                        viewMode: 'descriptions',
                      },
                      {
                        id: 'assigned_as',
                        name: 'Assigned As',
                        tooltip: 'assigned_as_description',
                      },
                    ]}
                    buttons={[
                      {
                        label: 'Edit',
                        action: 'edit',
                        onClick: items =>
                          showSideDialog('editSystem', items || selectedItems, editSystem),
                      },
                      {
                        label: 'Delete',
                        action: 'delete',
                        onClick: items =>
                          showModalDialog('deleteSystem', items || selectedItems, deleteSystem),
                      },
                    ]}
                    data={ownedItems}
                    idColumn="system_id"
                    selectedItems={selectedItems}
                    setSelectedItems={setSelectedItems}
                    loading={loadingTable}
                    editable={editableAdmin}
                    filterable={true}
                    filterObjects={[
                      'system_name',
                      'system_type',
                      'system_description',
                      'assigned_as',
                    ]}
                    showViewModes={true}
                  />
                </div>
              </Tabs>
            ) : (
              !loadingTable && <div className="no-result-text">No assigned assets</div>
            )}
          </div>
        </Tabs>
      </div>
    </div>
  );
};
